<div class="container py-4">

  <div class="page-header">
    <h1>Platform Admin</h1>
    <span class="badge">Dashboard</span>
  </div>

  <!-- Platform states / kill switches -->
  <div class="chart-card" data-testid="platform-states">
    <div class="chart-card__title">Platform states</div>
    @if (platformStatesError()) {
      <div class="state-msg state-msg--error">Failed to load platform states.</div>
    } @else if (platformStates() === null) {
      <div class="state-msg">Loading…</div>
    } @else {
      <ul class="switch-list">
        @for (state of platformStates(); track state.name) {
          <li class="switch-row" [class.switch-row--off]="state.data === false">
            <div class="switch-row__text">
              <span class="switch-row__name">{{ breakable(state.name) }}</span>
              @if (state.description) {
                <span class="switch-row__desc">{{ state.description }}</span>
              }
              <span class="switch-row__meta">Updated {{ formatLoginDate(state.updated_at) }}</span>
            </div>
            @if (isBoolean(state)) {
              <button type="button" class="switch" role="switch"
                [attr.aria-checked]="state.data === true"
                [attr.aria-label]="state.name"
                [class.switch--on]="state.data === true"
                [disabled]="savingState() === state.name"
                (click)="toggle(state)">
                <span class="switch__label">{{ state.data ? 'On' : 'Off' }}</span>
                <span class="switch__knob"></span>
              </button>
            } @else {
              <code class="switch-row__value">{{ formatData(state.data) }}</code>
            }
          </li>
        }
      </ul>
    }
  </div>

  @if (loading) {
    <div class="state-msg">Loading…</div>
  }
  @if (!loading && error) {
    <div class="state-msg state-msg--error">Failed to load stats.</div>
  }

  @if (!loading && !error && stats) {

    <!-- Stat cards -->
    <div class="section-title">Status Page Views</div>
    <div class="stats-row">
      <div class="stat-card">
        <span class="stat-card__label">Last 24 hours</span>
        <span class="stat-card__value">{{ stats.views_24h | number }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-card__label">Last 30 days</span>
        <span class="stat-card__value">{{ stats.views_30d | number }}</span>
      </div>
    </div>

    <div class="section-title">Monitor Checks</div>
    <div class="stats-row">
      <div class="stat-card">
        <span class="stat-card__label">Last 24 hours</span>
        <span class="stat-card__value">{{ stats.checks_24h | number }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-card__label">Last 30 days</span>
        <span class="stat-card__value">{{ stats.checks_30d | number }}</span>
      </div>
    </div>

    <!-- Views chart -->
    <div class="chart-card">
      <div class="chart-card__title">Status page views — last 30 days</div>
      <div class="chart-wrapper">
        <svg [attr.viewBox]="'0 0 ' + chartWidth + ' ' + chartHeight" class="chart-svg" xmlns="http://www.w3.org/2000/svg">
          <line *ngFor="let tick of viewsYTicks"
            [attr.x1]="paddingLeft" [attr.y1]="tick.y"
            [attr.x2]="chartWidth - paddingRight" [attr.y2]="tick.y"
            class="grid-line" />
          <text *ngFor="let tick of viewsYTicks"
            [attr.x]="paddingLeft - 4" [attr.y]="tick.y + 4"
            class="axis-label axis-label--y">{{ tick.value }}</text>
          <rect *ngFor="let bar of viewsBars"
            [attr.x]="bar.x" [attr.y]="bar.y"
            [attr.width]="bar.width" [attr.height]="bar.height"
            [class.bar--highlight]="tooltipViewsBar === bar"
            class="bar bar--views"
            (mouseenter)="tooltipViewsBar = bar"
            (mouseleave)="tooltipViewsBar = null" />
          <text *ngFor="let lbl of viewsXLabels"
            [attr.x]="lbl.x" [attr.y]="chartHeight - 4"
            class="axis-label axis-label--x">{{ lbl.label }}</text>
          @if (tooltipViewsBar) {
            <rect [attr.x]="tooltipX(tooltipViewsBar)" [attr.y]="tooltipY(tooltipViewsBar)"
              width="88" height="30" rx="5" class="tooltip-bg" />
            <text [attr.x]="tooltipTextX(tooltipViewsBar)" [attr.y]="tooltipViewsBar.y - 22" class="tooltip-date">
              {{ formatShort(tooltipViewsBar.data.date) }}
            </text>
            <text [attr.x]="tooltipTextX(tooltipViewsBar)" [attr.y]="tooltipViewsBar.y - 10" class="tooltip-count">
              {{ tooltipViewsBar.data.count }} view{{ tooltipViewsBar.data.count !== 1 ? 's' : '' }}
            </text>
          }
        </svg>
      </div>
    </div>

    <!-- Checks chart -->
    <div class="chart-card">
      <div class="chart-card__title">Monitor checks — last 30 days</div>
      <div class="chart-wrapper">
        <svg [attr.viewBox]="'0 0 ' + chartWidth + ' ' + chartHeight" class="chart-svg" xmlns="http://www.w3.org/2000/svg">
          <line *ngFor="let tick of checksYTicks"
            [attr.x1]="paddingLeft" [attr.y1]="tick.y"
            [attr.x2]="chartWidth - paddingRight" [attr.y2]="tick.y"
            class="grid-line" />
          <text *ngFor="let tick of checksYTicks"
            [attr.x]="paddingLeft - 4" [attr.y]="tick.y + 4"
            class="axis-label axis-label--y">{{ tick.value }}</text>
          <rect *ngFor="let bar of checksBars"
            [attr.x]="bar.x" [attr.y]="bar.y"
            [attr.width]="bar.width" [attr.height]="bar.height"
            [class.bar--highlight]="tooltipChecksBar === bar"
            class="bar bar--checks"
            (mouseenter)="tooltipChecksBar = bar"
            (mouseleave)="tooltipChecksBar = null" />
          <text *ngFor="let lbl of checksXLabels"
            [attr.x]="lbl.x" [attr.y]="chartHeight - 4"
            class="axis-label axis-label--x">{{ lbl.label }}</text>
          @if (tooltipChecksBar) {
            <rect [attr.x]="tooltipX(tooltipChecksBar)" [attr.y]="tooltipY(tooltipChecksBar)"
              width="88" height="30" rx="5" class="tooltip-bg" />
            <text [attr.x]="tooltipTextX(tooltipChecksBar)" [attr.y]="tooltipChecksBar.y - 22" class="tooltip-date">
              {{ formatShort(tooltipChecksBar.data.date) }}
            </text>
            <text [attr.x]="tooltipTextX(tooltipChecksBar)" [attr.y]="tooltipChecksBar.y - 10" class="tooltip-count">
              {{ tooltipChecksBar.data.count }} check{{ tooltipChecksBar.data.count !== 1 ? 's' : '' }}
            </text>
          }
        </svg>
      </div>
    </div>

    <!-- Recent users -->
    <div class="chart-card">
      <div class="chart-card__title">Users — sorted by last login</div>
      @if (stats.recent_users.length === 0) {
        <div class="state-msg">No users have logged in yet.</div>
      }
      @if (stats.recent_users.length > 0) {
        <table class="top-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th class="top-table__count">Last Login</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let user of stats.recent_users; let i = index">
              <td class="top-table__rank">{{ i + 1 }}</td>
              <td>
                <span class="top-table__title">{{ user.name }}</span>
                <span class="top-table__slug">{{ user.email }}</span>
              </td>
              <td class="top-table__count">{{ formatLoginDate(user.last_login) }}</td>
            </tr>
          </tbody>
        </table>
      }
    </div>

    <!-- Top 10 status pages -->
    <div class="chart-card">
      <div class="chart-card__title">Top 10 viewed status pages — last 30 days</div>
      @if (stats.top_pages.length === 0) {
        <div class="state-msg">No views recorded yet.</div>
      }
      @if (stats.top_pages.length > 0) {
        <table class="top-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Status Page</th>
              <th class="top-table__count">Views</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let page of stats.top_pages; let i = index">
              <td class="top-table__rank">{{ i + 1 }}</td>
              <td>
                <span class="top-table__title">{{ page.title }}</span>
                <span class="top-table__slug">{{ page.slug }}</span>
              </td>
              <td class="top-table__count">{{ page.count | number }}</td>
            </tr>
          </tbody>
        </table>
      }
    </div>

  }

</div>
